/**
 * B4 (E-1.B.1-04): Field Mapping UI
 *
 * Side-by-side: GHL fields (left) → Ease fields (right).
 * Auto-suggested mappings by name similarity.
 * Unmapped fields flagged in red. Type conversion warnings.
 */
import { useState, useEffect } from "react";
import { useTranslation } from "@/i18n";
import { apiFetch } from "@/lib/api-client";
import { cn } from "@/lib/utils";
import type { WizardState } from "../wizard-shell";

interface MapStepProps {
  state: WizardState;
  goNext: () => void;
  updateState: (patch: Partial<WizardState>) => void;
}

interface FieldMapping {
  sourceField: string;
  sourceType: string;
  targetField: string;
  targetType: string;
  transformType: string;
  confidence: number;
  suggestedByAi: boolean;
}

const EASE_TARGET_FIELDS = [
  { key: "firstName", label: "First Name", type: "text" },
  { key: "lastName", label: "Last Name", type: "text" },
  { key: "email", label: "Email", type: "email" },
  { key: "phone", label: "Phone", type: "phone" },
  { key: "company", label: "Company", type: "text" },
  { key: "tags", label: "Tags", type: "array" },
  { key: "address", label: "Address", type: "text" },
  { key: "city", label: "City", type: "text" },
  { key: "state", label: "State", type: "text" },
  { key: "postalCode", label: "Postal Code", type: "text" },
  { key: "country", label: "Country", type: "text" },
  { key: "dateOfBirth", label: "Date of Birth", type: "date" },
  { key: "source", label: "Source", type: "text" },
  { key: "notes", label: "Notes", type: "text" },
  { key: "", label: "— Skip field —", type: "" },
];

export function MapStep({ state, updateState }: MapStepProps) {
  const { t } = useTranslation();
  const [mappings, setMappings] = useState<FieldMapping[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (state.fieldMappings || !state.jobId) return;

    setIsLoading(true);
    apiFetch<{ mappings: FieldMapping[] }>(
      `/platform/migration/${state.jobId}/suggest-mappings`,
    )
      .then((result) => {
        setMappings(result.mappings);
        updateState({
          fieldMappings: result.mappings.map((m) => ({
            sourceField: m.sourceField,
            targetField: m.targetField,
            transformType: m.transformType,
          })),
        });
      })
      .catch(() => {
        // Fallback: empty mappings
        setMappings([]);
      })
      .finally(() => setIsLoading(false));
  }, [state.jobId, state.fieldMappings, updateState]);

  const updateMapping = (idx: number, targetField: string) => {
    const updated = [...mappings];
    const existing = updated[idx];
    if (!existing) return;
    updated[idx] = { ...existing, targetField };
    setMappings(updated);
    updateState({
      fieldMappings: updated.map((m) => ({
        sourceField: m.sourceField,
        targetField: m.targetField,
        transformType: m.transformType,
      })),
    });
  };

  const unmappedCount = mappings.filter((m) => !m.targetField).length;

  if (isLoading) {
    return (
      <div className="flex flex-col items-center gap-4 py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        <p className="text-sm text-muted-foreground">{t("migration.map.analyzing")}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-foreground">{t("migration.map.title")}</h2>
        <p className="mt-1 text-sm text-muted-foreground">{t("migration.map.description")}</p>
      </div>

      {unmappedCount > 0 && (
        <div className="rounded-md bg-[var(--color-warning)]/5 px-3 py-2 text-sm text-[var(--color-warning)]">
          {t("migration.map.unmappedWarning", { count: unmappedCount })}
        </div>
      )}

      <div className="space-y-2">
        {/* Header */}
        <div className="grid grid-cols-12 gap-3 px-3 text-xs font-semibold text-muted-foreground uppercase">
          <div className="col-span-4">{t("migration.map.sourceField")}</div>
          <div className="col-span-1 text-center">→</div>
          <div className="col-span-4">{t("migration.map.targetField")}</div>
          <div className="col-span-3">{t("migration.map.confidence")}</div>
        </div>

        {/* Mapping rows */}
        {mappings.map((mapping, idx) => (
          <div
            key={mapping.sourceField}
            className={cn(
              "grid grid-cols-12 items-center gap-3 rounded-lg border p-3",
              !mapping.targetField ? "border-[var(--color-destructive)]/30 bg-[var(--color-destructive)]/5" : "border-border",
            )}
          >
            <div className="col-span-4">
              <p className="text-sm font-medium text-foreground">{mapping.sourceField}</p>
              <p className="text-xs text-muted-foreground">{mapping.sourceType}</p>
            </div>
            <div className="col-span-1 text-center text-muted-foreground">→</div>
            <div className="col-span-4">
              <select
                value={mapping.targetField}
                onChange={(e) => updateMapping(idx, e.target.value)}
                className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm"
              >
                {EASE_TARGET_FIELDS.map((field) => (
                  <option key={field.key} value={field.key}>
                    {field.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-span-3 flex items-center gap-2">
              {mapping.suggestedByAi && (
                <span className="rounded bg-[var(--color-primary)]/10 px-1.5 py-0.5 text-xs text-[var(--color-primary)]">AI</span>
              )}
              <div className="flex-1 h-1.5 rounded-full bg-muted">
                <div
                  className={cn(
                    "h-1.5 rounded-full",
                    mapping.confidence > 0.8 ? "bg-[var(--color-success)]/50" :
                    mapping.confidence > 0.5 ? "bg-[var(--color-warning)]/50" : "bg-[var(--color-destructive)]/50",
                  )}
                  style={{ width: `${mapping.confidence * 100}%` }}
                />
              </div>
              <span className="text-xs text-muted-foreground">
                {Math.round(mapping.confidence * 100)}%
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
